import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from '@/pages/shared/base-query-auth';
import { runMutationOnce, runQueryOnce } from '@/pages/shared/rtk-query-helpers';

/**
 * Builds the support message multipart body. Exported so it can be unit tested directly:
 * jsdom's `fetch` hangs indefinitely when asked to stream a multipart body containing a
 * `File`, so the wire call itself cannot be asserted in this test environment.
 */
export const buildSendMessageBody = ({
    requestType,
    title,
    message,
    pageUrl,
    userAgent,
    siteId,
    attachments,
}) => {
    const formData = new FormData();
    formData.append('requestType', requestType);
    formData.append('title', title);
    formData.append('message', message);
    if (pageUrl) formData.append('pageUrl', pageUrl);
    if (userAgent) formData.append('userAgent', userAgent);
    if (siteId) formData.append('siteId', siteId);

    (attachments || []).forEach((file) => {
        const uploadFile = file.originFileObj || file;
        formData.append('attachments', uploadFile, uploadFile.name);
    });

    return formData;
};

export const aboutApi = createApi({
    reducerPath: 'aboutApi',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getReleaseNotes: builder.query({
            query: () => ({ url: '/api/about/releaseNotes', method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        getReleaseNotification: builder.query({
            query: () => ({ url: '/api/about/releaseNotification', method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        getRoadmapItems: builder.query({
            query: () => ({ url: '/api/about/roadmap', method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        sendMessage: builder.mutation({
            query: (model) => ({
                url: '/api/about/sendMessage',
                method: 'POST',
                body: buildSendMessageBody(model),
            }),
        }),
    }),
});

/**
 * Runs an `aboutApi` query once and releases its cache subscription.
 */
export const runAboutQuery = (dispatch, endpointName, arg) =>
    runQueryOnce(dispatch, aboutApi.endpoints[endpointName], arg);

/**
 * Sends a support message and resolves with the server payload.
 */
export const sendSupportMessage = (dispatch, model) =>
    runMutationOnce(dispatch, aboutApi.endpoints.sendMessage, model);

export default aboutApi;
